/**
 * Voice controller — browser SpeechRecognition STT + ElevenLabs TTS.
 *
 * Flow:
 * 1. User clicks mic → getUserMedia permission check → start SpeechRecognition
 * 2. SpeechRecognition fires interim results → show transcription in UI
 * 3. On final result → call sendChatMessage(text) → agent processes it normally
 * 4. Agent response text → POST /api/tts → play audio
 * 5. User clicks stop or conversation ends → cleanup
 *
 * The actual agent conversation goes through the normal /api/chat path so
 * memory, history, and tool calls all work exactly as in text chat.
 */

export type VoiceState =
  | "idle"
  | "connecting"
  | "listening"
  | "speaking"
  | "processing"
  | "ending";

export type VoiceEventPayload =
  | { event: "voice.started"; session_id: string; input_sample_rate: number; output_sample_rate: number }
  | { event: "voice.transcription"; text: string; is_final: boolean }
  | { event: "voice.tool_call"; name: string; args: unknown }
  | { event: "voice.tool_result"; name: string; success: boolean }
  | { event: "voice.status"; state: VoiceState }
  | { event: "voice.ended"; reason: string }
  | { event: "voice.error"; message: string };

export interface VoiceController {
  state: VoiceState;
  transcription: string;
  start(): Promise<void>;
  stop(): Promise<void>;
  destroy(): void;
}

interface VoiceCallbacks {
  /** Send a text message to the agent (same path as normal chat). */
  sendChatMessage(text: string): void;
  /** Called when voice state changes. */
  onStateChange(state: VoiceState): void;
  /** Called with interim/final transcription text. */
  onTranscription(text: string, isFinal: boolean): void;
  /** Called on error. */
  onError(message: string): void;
  /** Base URL for API calls (e.g. "https://u-abc.sontara.ai"). Empty = same origin. */
  baseUrl?: string;
  /** Bearer token for /api/tts auth. */
  token?: string;
}

// Web Speech API type declarations (not in all TS lib versions)
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
declare const SpeechRecognition: { new(): SpeechRecognitionInstance } | undefined;
declare const webkitSpeechRecognition: { new(): SpeechRecognitionInstance } | undefined;

function getSpeechRecognition(): (new () => SpeechRecognitionInstance) | null {
  if (typeof SpeechRecognition !== "undefined") return SpeechRecognition;
  if (typeof webkitSpeechRecognition !== "undefined") return webkitSpeechRecognition;
  return null;
}

export function createVoiceController(callbacks: VoiceCallbacks): VoiceController {
  let state: VoiceState = "idle";
  let transcription = "";

  let recognition: SpeechRecognitionInstance | null = null;
  let audioContext: AudioContext | null = null;
  let activeAudioSource: AudioBufferSourceNode | null = null;
  // Track whether the mic is intentionally running (to auto-restart after TTS)
  let micActive = false;

  function setState(newState: VoiceState) {
    state = newState;
    callbacks.onStateChange(newState);
  }

  async function start() {
    if (state !== "idle") return;

    // Check mic permission first (also ensures HTTPS)
    if (!navigator.mediaDevices?.getUserMedia) {
      const isLocalhost = location.hostname === "localhost" || location.hostname === "127.0.0.1";
      callbacks.onError(
        isLocalhost
          ? "Microphone access unavailable. Check browser permissions."
          : "Voice requires HTTPS. Open this page via https:// to use voice chat.",
      );
      return;
    }

    setState("connecting");

    // Trigger mic permission prompt
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop()); // We only needed the permission
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Microphone permission denied";
      callbacks.onError(msg);
      setState("idle");
      return;
    }

    const RecognitionCtor = getSpeechRecognition();
    if (!RecognitionCtor) {
      callbacks.onError("Voice is not supported in this browser. Try Chrome or Edge.");
      setState("idle");
      return;
    }

    micActive = true;
    startRecognition(RecognitionCtor);
  }

  function startRecognition(RecognitionCtor: new () => SpeechRecognitionInstance) {
    if (!micActive) return;

    recognition = new RecognitionCtor();
    recognition.continuous = false; // single utterance mode for push-to-talk feel
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setState("listening");
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimText = "";
      let finalText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        if (result.isFinal) {
          finalText += text;
        } else {
          interimText += text;
        }
      }

      if (interimText) {
        transcription = interimText;
        callbacks.onTranscription(interimText, false);
      }

      if (finalText.trim()) {
        transcription = finalText.trim();
        callbacks.onTranscription(finalText.trim(), true);
        // Send to agent
        setState("processing");
        callbacks.sendChatMessage(finalText.trim());
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // "no-speech" and "aborted" are expected; don't surface as errors
      if (event.error === "no-speech") {
        // Restart automatically
        return;
      }
      if (event.error === "aborted") return;
      callbacks.onError(`Speech recognition error: ${event.error}`);
    };

    recognition.onend = () => {
      // If we're not speaking/processing and mic is still active, restart listening
      if (micActive && state !== "idle" && state !== "ending" && state !== "speaking") {
        // Small delay to avoid tight loops on rapid end events
        setTimeout(() => {
          if (micActive && recognition !== null) {
            startRecognition(RecognitionCtor);
          }
        }, 200);
      }
    };

    try {
      recognition.start();
    } catch {
      // Already started — ignore
    }
  }

  /** Called by app-gateway when the agent's response text is ready. */
  async function handleAgentResponse(text: string) {
    if (state === "idle" || state === "ending") return;
    if (!text.trim()) {
      // No text to speak — go back to listening
      if (micActive) setState("listening");
      return;
    }

    setState("speaking");

    // Pause mic while speaking to avoid feeding playback back into recognition
    if (recognition) {
      recognition.abort();
      recognition = null;
    }

    try {
      const baseUrl = callbacks.baseUrl ?? "";
      const url = `${baseUrl}/api/tts`;
      const fetchInit: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(callbacks.token ? { Authorization: `Bearer ${callbacks.token}` } : {}),
        },
        body: JSON.stringify({ text }),
      };

      const resp = await fetch(url, fetchInit);
      if (!resp.ok) {
        const errText = await resp.text().catch(() => "");
        throw new Error(`TTS error ${resp.status}: ${errText}`);
      }

      const arrayBuffer = await resp.arrayBuffer();
      if (!audioContext || audioContext.state === "closed") {
        audioContext = new AudioContext();
      }

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      if (activeAudioSource) {
        activeAudioSource.stop();
        activeAudioSource = null;
      }

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      activeAudioSource = source;

      source.onended = () => {
        activeAudioSource = null;
        // After speaking, resume listening if mic is still active
        if (micActive && state !== "idle" && state !== "ending") {
          const Ctor = getSpeechRecognition();
          if (Ctor) {
            setState("listening");
            startRecognition(Ctor);
          }
        }
      };

      source.start();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "TTS failed";
      // Log the TTS error but don't surface it to the user — just resume listening
      console.warn("[voice] TTS error:", msg);
      if (micActive) {
        const Ctor = getSpeechRecognition();
        if (Ctor) {
          setState("listening");
          startRecognition(Ctor);
        }
      }
    }
  }

  async function stop() {
    if (state === "idle") return;
    setState("ending");
    micActive = false;
    cleanup();
    setState("idle");
  }

  function cleanup() {
    micActive = false;
    if (recognition) {
      recognition.abort();
      recognition = null;
    }
    if (activeAudioSource) {
      activeAudioSource.stop();
      activeAudioSource = null;
    }
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(() => {});
      audioContext = null;
    }
    transcription = "";
  }

  return {
    get state() {
      return state;
    },
    get transcription() {
      return transcription;
    },
    start,
    stop,
    destroy: cleanup,
    handleAgentResponse,
  } as VoiceController & {
    handleAgentResponse: (text: string) => Promise<void>;
  };
}
